import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  House,
  TrendingUp,
  Wallet,
  CircleUserRound,
  Building2,
  FileQuestion,
  ArrowRight,
  Check,
  Lock,
  FileCheck,
  Server,
} from 'lucide-react';

import Header from '../components/common/landingPage/Header';
import { useForceLightTheme } from '../lib/theme';
import Footer from '../components/common/landingPage/Footer';
import FaqItem from '../components/common/landingPage/FaqItem';
import WhatsApp from '../components/common/landingPage/WhatsApp';

import NeutronIcon from '../assets/images/landing-page/neutron.png';
import BridgeIcon from '../assets/images/landing-page/bridge.png';
import AwsIcon from '../assets/images/landing-page/aws.png';
import FireblocksIcon from '../assets/images/landing-page/fireblocks.png';

import GooglePlayLogo from '../assets/images/landing-page/googlePlayLogo.svg';
import AppStoreLogo from '../assets/images/landing-page/appStoreLogo.svg';

import ColombiaFlag from '../assets/images/country/colombia.png';
import MexicoFlag from '../assets/images/country/mexico.png';
import UsFlag from '../assets/images/country/us.png';
import EuropeFlag from '../assets/images/country/europe.png';
import BrazilFlag from '../assets/images/country/brazil.png';

import LogoColor from '../assets/images/logo-flowex-color-cropped.png';

import VideoNyc from '../assets/videos/markets-nyc.mp4';
import VideoBogota from '../assets/videos/markets-bogota.mp4';
import VideoTrading from '../assets/videos/markets-trading.mp4';
import VideoPuerto from '../assets/videos/markets-puerto.mp4';
import VideoAeropuerto from '../assets/videos/markets-aeropuerto.mp4';

const Partners = [
  { image: BridgeIcon, name: 'Bridge' },
  { image: FireblocksIcon, name: 'Fireblocks' },
  { image: AwsIcon, name: 'AWS' },
  { image: NeutronIcon, name: 'Neutron' },
];

const Apps = [
  {
    icon: GooglePlayLogo,
    description: 'Disponible en',
    platform: 'Google Play',
    url: 'https://play.google.com/store/apps/details?id=com.flowexpay.app',
  },
  {
    icon: AppStoreLogo,
    description: 'Descárgala en la',
    platform: 'App Store',
    url: 'https://apps.apple.com/app/flowexpay/id6762288494',
  },
];

const HeroPills = [
  'Acciones CO + USA',
  'Fracciones desde $50 mil',
  'Cuentas USD·EUR·MXN·BRL',
  'Metas · Agentes · Tesorería',
];

const AccountFlags = [
  { flag: UsFlag, label: 'USA · USD' },
  { flag: EuropeFlag, label: 'Europa · EUR' },
  { flag: MexicoFlag, label: 'México · MXN' },
  { flag: BrazilFlag, label: 'Brasil · BRL (PIX)' },
  { flag: ColombiaFlag, label: 'Colombia · COP' },
];

const Faqs = [
  {
    question: '¿Qué es FlowexPay y qué servicios ofrece?',
    answer:
      'FlowexPay es una plataforma colombiana creada por Loaded Colombia SAS. Te permite invertir en Colombia y Estados Unidos, mover dinero con cuentas virtuales en USD, EUR, MXN y BRL, usar Metas y Agentes, y operar tesorería Business — todo desde una sola app.',
  },
  {
    question: '¿Por qué elegir FlowexPay?',
    answer:
      'Una sola app para invertir, enviar, recibir y cobrar. Cuentas virtuales globales, fracciones desde montos accesibles, Oráculo en español y socios institucionales como Bridge, Fireblocks y AWS.',
  },
  {
    question: '¿Cómo empiezo a usar FlowexPay?',
    answer:
      'Regístrate en flowexpay.com o descarga la app, verifica tu identidad y recarga vía PSE o desde cuentas en EE. UU., Europa o México. En pocos minutos estás listo.',
  },
  {
    question: '¿Cuánto cuesta enviar dinero con FlowexPay?',
    answer:
      'La comisión es del 0,5% de entrada y 0,5% de salida (1% total por operación), transparente y sin comisiones ocultas. Puedes ver el costo exacto antes de confirmar cada transferencia.',
  },
  {
    question: '¿Es seguro usar FlowexPay?',
    answer:
      'Sí. Estamos respaldados por socios regulados a nivel institucional: Bridge (custodia y cumplimiento), Fireblocks (seguridad de activos), AWS (infraestructura), y cumplimos con las regulaciones UIAF/KYC colombianas.',
  },
];

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
}

function CurrencyCalculator() {
  const [cop, setCop] = useState(1000000);
  const rate = 0.000243;
  const feeIn = 0.005;
  const feeOut = 0.005;
  const copAfterIn = cop * (1 - feeIn);
  const usdGross = copAfterIn * rate;
  const usd = (usdGross * (1 - feeOut)).toFixed(2);
  const totalFeeCop = Math.round(cop * (feeIn + feeOut - feeIn * feeOut));
  return (
    <div className="lo-card p-8 md:p-10 max-w-xl mx-auto">
      <p className="text-sm font-medium lo-muted mb-2">Tú envías</p>
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#2A59C6]/12">
        <input
          type="number"
          value={cop}
          onChange={(e) => setCop(Math.max(0, Number(e.target.value) || 0))}
          className="text-3xl md:text-4xl font-bold text-[#0B1B3A] bg-transparent outline-none w-full"
        />
        <div className="flex items-center gap-2 shrink-0 bg-[#E8F0FF] px-4 py-2 rounded-full">
          <img src={ColombiaFlag} alt="COP" className="w-6 h-6 rounded-full object-cover" />
          <span className="font-semibold text-[#092F7B]">COP</span>
        </div>
      </div>
      <p className="text-sm font-medium lo-muted mb-2">Ellos reciben</p>
      <div className="flex items-center justify-between">
        <p className="text-3xl md:text-4xl font-bold text-[#2A59C6]">${usd}</p>
        <div className="flex items-center gap-2 shrink-0 bg-[#E8F0FF] px-4 py-2 rounded-full">
          <img src={UsFlag} alt="USD" className="w-6 h-6 rounded-full object-cover" />
          <span className="font-semibold text-[#092F7B]">USD</span>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-[#2A59C6]/12 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="lo-muted">Tasa de cambio</span>
          <span className="font-medium text-[#0B1B3A]">
            1 USD ≈ {Math.round(1 / rate).toLocaleString()} COP
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="lo-muted">Comisión entrada (0,5%)</span>
          <span className="font-medium text-[#0B1B3A]">
            {Math.round(cop * feeIn).toLocaleString()} COP
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="lo-muted">Comisión salida (0,5%)</span>
          <span className="font-medium text-[#0B1B3A]">
            ≈ {Math.round(copAfterIn * rate * feeOut * (1 / rate)).toLocaleString()} COP
          </span>
        </div>
        <div className="flex justify-between text-sm pt-2 mt-2 border-t border-[#2A59C6]/12">
          <span className="text-[#2A59C6] font-semibold">Total comisión</span>
          <span className="text-[#2A59C6] font-bold">
            {totalFeeCop.toLocaleString()} COP (1%)
          </span>
        </div>
      </div>
    </div>
  );
}

function VideoCaption({ title, children }) {
  return (
    <div className="absolute left-3.5 right-3.5 bottom-3.5 z-20 rounded-2xl border border-white/15 bg-[#0B1B3A]/80 backdrop-blur-md px-3.5 py-3 text-white">
      <p className="text-sm font-bold mb-0.5">{title}</p>
      <p className="text-xs text-white/80 leading-snug">{children}</p>
    </div>
  );
}

export default function HomePage() {
  useForceLightTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const menus = [
    { title: 'Inicio', minWidth: '42px', ref: useRef(null), icon: (s) => <House className={s} /> },
    { title: 'Invertir', minWidth: '62px', ref: useRef(null), icon: (s) => <TrendingUp className={s} /> },
    { title: 'Cuentas', minWidth: '62px', ref: useRef(null), icon: (s) => <Wallet className={s} /> },
    { title: 'Personas', minWidth: '67px', ref: useRef(null), icon: (s) => <CircleUserRound className={s} /> },
    { title: 'Business', minWidth: '72px', ref: useRef(null), icon: (s) => <Building2 className={s} /> },
    { title: 'FAQ', minWidth: '36px', ref: useRef(null), icon: (s) => <FileQuestion className={s} /> },
  ];

  const onSelectMenuBar = (idx) => {
    setActiveTab(idx);
    menus[Math.min(idx, menus.length - 1)].ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-ola relative w-full min-h-screen">
      <Header activeTab={activeTab} onSelectMenuBar={onSelectMenuBar} menus={menus} />

      {/* HERO — NYC */}
      <section ref={menus[0].ref} className="lo-hero">
        <video
          src={VideoNyc}
          autoPlay
          muted
          loop
          playsInline
          className="lo-hero__video"
        />
        <div className="lo-hero__overlay" />
        <div className="lo-hero__glow" style={{ top: '12%', right: '8%' }} />
        <div className="lo-hero__glow" style={{ bottom: '8%', left: '4%', animationDelay: '2s' }} />

        <div className="relative z-10 min-h-[100vh] min-h-[100dvh] flex items-end py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <Reveal>
              <div className="max-w-3xl space-y-6">
                <img
                  src={LogoColor}
                  alt="FlowexPay"
                  className="h-11 md:h-12 w-auto object-contain bg-transparent drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
                />
                <div className="lo-pill">
                  <span className="w-2 h-2 bg-[#6B9BFF] rounded-full animate-pulse" />
                  Una app · Colombia y el mundo · iOS y Android
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white">
                  Tu dinero global:{' '}
                  <span className="lo-hero-accent">invertir, mover, cobrar y hacer crecer.</span>
                </h1>
                <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl">
                  Acciones y fondos en Colombia y USA (también fracciones desde $50.000 COP).
                  Cuentas virtuales en USA, Europa, México y Brasil. Metas, agentes, Oráculo y
                  tesorería Business — todo en FlowexPay.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {HeroPills.map((pill) => (
                    <span key={pill} className="lo-pill text-xs md:text-sm">
                      {pill}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="lo-btn-primary group inline-flex items-center gap-2 px-8 py-3.5 font-semibold rounded-full cursor-pointer"
                  >
                    Abrir cuenta gratis
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="lo-btn-ghost px-8 py-3.5 font-semibold rounded-full cursor-pointer"
                  >
                    Iniciar sesión
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 pt-1">
                  {Apps.map((app, i) => (
                    <a
                      key={i}
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-5 py-3 rounded-xl transition-all hover:scale-[1.02] border border-white/20 bg-[#0B1B3A]/45 hover:bg-[#0B1B3A]/65 backdrop-blur-md text-white"
                    >
                      <img src={app.icon} alt={app.platform} className="w-7 h-7" />
                      <div className="text-left">
                        <p className="text-[10px] text-white/70">{app.description}</p>
                        <p className="text-sm font-semibold">{app.platform}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#6B9BFF] rounded-full" />
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="lo-partners">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <p className="text-center text-sm font-medium lo-muted mb-6 tracking-wide uppercase">
            Respaldado por líderes globales en tecnología financiera
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {Partners.map((p, i) => (
              <img
                key={i}
                src={p.image}
                alt={p.name}
                className="h-8 md:h-10 grayscale opacity-55 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
              />
            ))}
          </div>
        </div>
      </section>

      {/* INVERTIR */}
      <section id="invertir" ref={menus[1].ref} className="lo-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="lo-eyebrow mb-4">01 — Invertir</p>
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight text-[#0B1B3A] max-w-3xl">
              Mercados de Colombia y de{' '}
              <span className="text-[#2A59C6]">Estados Unidos</span>
            </h2>
            <p className="mt-5 text-lg lo-muted max-w-3xl leading-relaxed">
              No solo Wall Street: también fondos y acciones colombianas. Empieza pequeño —
              fracciones desde cincuenta mil pesos.
            </p>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            <Reveal>
              <div className="relative">
                <div className="lo-media-ring" />
                <div className="lo-media lo-media--wide" style={{ aspectRatio: '16 / 10' }}>
                  <video src={VideoTrading} autoPlay muted loop playsInline />
                  <VideoCaption title="Mercado de capitales">
                    Desk serio · Invertir CO + USA en la misma app.
                  </VideoCaption>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="lo-card p-5">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#2A59C6] mb-2">
                    Colombia
                  </p>
                  <h3 className="text-lg font-bold text-[#0B1B3A] mb-2">Acciones y fondos CO</h3>
                  <p className="text-sm lo-muted leading-relaxed">
                    Invierte en el mercado local: acciones y fondos colombianos dentro de la misma
                    app.
                  </p>
                </div>
                <div className="lo-card p-5">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#2A59C6] mb-2">
                    USA
                  </p>
                  <h3 className="text-lg font-bold text-[#0B1B3A] mb-2">Acciones y ETFs</h3>
                  <p className="text-sm lo-muted leading-relaxed">
                    Mercado de Estados Unidos con broker-dealer regulado. Análisis + Oráculo en
                    español.
                  </p>
                </div>
                <div className="lo-card p-6 sm:col-span-2">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#2A59C6] mb-2">
                    Accesible
                  </p>
                  <p className="text-3xl font-bold text-[#2A59C6] mb-1">Desde $50.000 COP</p>
                  <h3 className="text-lg font-bold text-[#0B1B3A] mb-2">
                    Fracciones de una acción
                  </h3>
                  <p className="text-sm lo-muted leading-relaxed">
                    No necesitas comprar la acción completa. Entra al mercado con un monto que sí
                    cabe en tu vida.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CUENTAS VIRTUALES */}
      <section id="cuentas" ref={menus[2].ref} className="lo-section lo-section--navy overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="lo-eyebrow mb-4">02 — Cuentas virtuales globales</p>
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight max-w-4xl">
              USA, Europa, México y Brasil —{' '}
              <span className="text-[#6B9BFF]">sin salir de Colombia</span>
            </h2>
            <p className="mt-5 text-lg text-white/75 max-w-3xl leading-relaxed">
              Recibe y mueve como si tuvieras cuenta local en cada región. Más COP para el día a
              día.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <div className="mt-8 flex flex-wrap gap-3">
              {AccountFlags.map((a) => (
                <div
                  key={a.label}
                  className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md"
                >
                  <img src={a.flag} alt="" className="w-6 h-6 rounded-full object-cover" />
                  <span className="text-sm font-semibold text-white">{a.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <Reveal>
              <div className="relative">
                <div className="lo-media lo-media--wide" style={{ aspectRatio: '16 / 10' }}>
                  <video src={VideoAeropuerto} autoPlay muted loop playsInline />
                  <VideoCaption title="Movimiento internacional">
                    La misma lógica que viaja tu plata.
                  </VideoCaption>
                </div>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="relative">
                <div className="lo-media lo-media--wide" style={{ aspectRatio: '16 / 10' }}>
                  <video src={VideoPuerto} autoPlay muted loop playsInline />
                  <VideoCaption title="Comercio y cobros">
                    Importar, exportar, cobrar clientes afuera.
                  </VideoCaption>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {[
              {
                title: 'Enviar',
                text: 'Al mundo en minutos. Tasas transparentes. Retiro COP nativo sin fee al cliente.',
              },
              {
                title: 'Recibir',
                text: 'Cuentas virtuales para que te paguen en divisa local.',
              },
              {
                title: 'Convertir',
                text: 'COP↔USD y otras rutas con fee claro — sin letra chica de banco.',
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="lo-card lo-card--glass h-full p-6">
                  <h3 className="text-lg font-bold mb-2">{c.title}</h3>
                  <p className="text-sm text-white/75 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section id="personas" ref={menus[3].ref} className="lo-section lo-section--soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="lo-eyebrow mb-4">03 — Personas</p>
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight text-[#0B1B3A] max-w-3xl">
              Metas, agentes y Oráculo —{' '}
              <span className="text-[#2A59C6]">tu copiloto</span>
            </h2>
            <p className="mt-5 text-lg lo-muted max-w-3xl leading-relaxed">
              No es solo una wallet. Es cómo tu plata trabaja con permiso, voz y plan.
            </p>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            <Reveal>
              <div className="relative">
                <div className="lo-media-ring" />
                <div className="lo-media lo-media--wide" style={{ aspectRatio: '16 / 10' }}>
                  <video src={VideoBogota} autoPlay muted loop playsInline />
                  <VideoCaption title="Desde Bogotá, con ambición">
                    App iOS y Android en el bolsillo.
                  </VideoCaption>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  {
                    tag: 'Metas',
                    title: 'Mis Metas',
                    text: 'Objetivos en COP o USD, proyección y seguimiento. Ahorro con destino.',
                  },
                  {
                    tag: 'Agentes',
                    title: 'Mis Agentes',
                    text: 'Constante, Paciente, Cazador, Guardián. Proponen; tú apruebas (pide permiso).',
                  },
                  {
                    tag: 'Oráculo',
                    title: 'Chat + voz',
                    text: 'Te explica el mercado y tu plata en español. WhatsApp y app.',
                  },
                  {
                    tag: 'Portafolio',
                    title: 'Todo junto',
                    text: 'Posiciones CO + USA, saldos y movimiento sin ruido.',
                  },
                ].map((c) => (
                  <div key={c.title} className="lo-card p-5">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#2A59C6] mb-2">
                      {c.tag}
                    </p>
                    <h3 className="text-lg font-bold text-[#0B1B3A] mb-2">{c.title}</h3>
                    <p className="text-sm lo-muted leading-relaxed">{c.text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BUSINESS */}
      <section id="business" ref={menus[4].ref} className="lo-section lo-section--navy overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="lo-eyebrow mb-4">04 — Business · Tesorería</p>
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight max-w-3xl">
              Cobrar, pagar y caja{' '}
              <span className="text-[#6B9BFF]">para la empresa</span>
            </h2>
            <p className="mt-5 text-lg text-white/75 max-w-3xl leading-relaxed">
              Tesorería que respira: cuentas globales, cartera, nómina, provisiones y calendario —
              Oráculo Business incluido.
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                tag: 'Cobrar',
                title: 'Cartera + PSE',
                text: 'Links de cobro, mora, recordatorios. El cliente no necesita Flowex.',
              },
              {
                tag: 'Pagar',
                title: 'Nómina / dispersión',
                text: 'Pagos con SMS del dueño. Destinos guardados.',
              },
              {
                tag: 'Caja',
                title: 'Salud de caja',
                text: 'Visibilidad de flujo, provisiones y calendario tributario.',
              },
              {
                tag: 'Equipo',
                title: 'Roles',
                text: 'Maker-checker y control para operadores vs dueño.',
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 70}>
                <div className="lo-card lo-card--glass h-full p-6">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#6B9BFF] mb-2">
                    {c.tag}
                  </p>
                  <h3 className="text-lg font-bold mb-2">{c.title}</h3>
                  <p className="text-sm text-white/75 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAPA DUAL */}
      <section className="lo-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="lo-eyebrow mb-4">Todo FlowexPay de un vistazo</p>
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight text-[#0B1B3A]">
              Una plataforma,{' '}
              <span className="text-[#2A59C6]">dos mundos</span>
            </h2>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="lo-card h-full p-8">
                <p className="text-[11px] uppercase tracking-wider font-bold text-[#2A59C6] mb-3">
                  Personas
                </p>
                <h3 className="text-2xl font-bold text-[#0B1B3A] mb-4">Para ti</h3>
                <ul className="space-y-3 text-[#0B1B3A]/90">
                  {[
                    'Invertir CO + USA (fracciones desde $50.000)',
                    'Enviar / recibir / convertir',
                    'Cuentas virtuales USD·EUR·MXN·BRL',
                    'Metas · Agentes · Oráculo · Portafolio',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="lo-check mt-0.5">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="lo-btn-primary mt-8 inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-full cursor-pointer"
                >
                  Abrir cuenta personal
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full p-8 rounded-3xl text-white relative overflow-hidden shadow-2xl bg-gradient-to-br from-[#0B1B3A] via-[#092F7B] to-[#2A59C6]">
                <p className="text-[11px] uppercase tracking-wider font-bold text-[#6B9BFF] mb-3">
                  Business
                </p>
                <h3 className="text-2xl font-bold mb-4">Para tu empresa</h3>
                <ul className="space-y-3 text-white/90">
                  {[
                    'Cuentas globales',
                    'Cobrar (cartera / PSE)',
                    'Pagar / nómina',
                    'Caja · Provisiones · Calendario',
                    'Oráculo Business (chat + voz)',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#6B9BFF]/20 text-[#6B9BFF] shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-full cursor-pointer bg-white text-[#0B1B3A] hover:bg-[#F4F7FC] transition-colors"
                >
                  Abrir cuenta Business
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TASAS / CALCULATOR */}
      <section className="lo-section lo-section--soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="space-y-6">
              <p className="lo-eyebrow">Tasas transparentes</p>
              <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight text-[#0B1B3A]">
                Las tasas que tu banco{' '}
                <span className="lo-soft">preferiría que no vieras.</span>
              </h2>
              <p className="text-lg lo-muted leading-relaxed">
                Sin márgenes inflados. Sin comisiones ocultas. Pruébalo tú mismo.
              </p>
              <ul className="space-y-3">
                {[
                  '0,5% entrada + 0,5% salida — 1% total',
                  'Costo visible antes de confirmar',
                  'Retiro COP nativo sin fee al cliente',
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="lo-check">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-[#0B1B3A]/90">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <CurrencyCalculator />
          </Reveal>
        </div>
      </section>

      {/* SEGURIDAD */}
      <section className="lo-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <p className="lo-eyebrow mb-4">Seguridad institucional</p>
              <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight text-[#0B1B3A]">
                Tu dinero, resguardado por los{' '}
                <span className="text-[#2A59C6]">mismos que protegen a los bancos.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Lock,
                partner: FireblocksIcon,
                title: 'Custodia institucional',
                description:
                  'Activos custodiados por Fireblocks, la plataforma usada por instituciones financieras globales.',
              },
              {
                icon: FileCheck,
                partner: BridgeIcon,
                title: 'Cumplimiento regulado',
                description:
                  'Operamos con Bridge más licencias locales y registro UIAF/KYC en Colombia.',
              },
              {
                icon: Server,
                partner: AwsIcon,
                title: 'Infraestructura bancaria',
                description:
                  'Plataforma sobre Amazon Web Services, con cifrado y alta disponibilidad.',
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="lo-card h-full p-8 group">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-[#E8F0FF] rounded-2xl flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-[#2A59C6]" />
                    </div>
                    <img
                      src={item.partner}
                      alt="partner"
                      className="h-7 opacity-70 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all"
                    />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#0B1B3A]">{item.title}</h3>
                  <p className="lo-muted leading-relaxed">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative lo-section overflow-hidden bg-gradient-to-br from-[#0B1B3A] via-[#092F7B] to-[#2A59C6]">
        <div className="absolute inset-0 opacity-25">
          <video
            src={VideoNyc}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3A]/60 via-[#092F7B]/50 to-[#0B1B3A]/80" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center text-white">
          <Reveal>
            <img
              src={LogoColor}
              alt="FlowexPay"
              className="h-10 w-auto mx-auto mb-6 object-contain bg-transparent"
            />
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight">
              Empieza a mover tu dinero global{' '}
              <span className="text-[#6B9BFF]">hoy.</span>
            </h2>
            <p className="mt-5 text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              Personas o Business — misma app, mismo estándar. Disponible en iOS y Android.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="lo-btn-primary inline-flex items-center gap-2 px-8 py-3.5 font-semibold rounded-full cursor-pointer"
              >
                Registrarse
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => onSelectMenuBar(4)}
                className="lo-btn-ghost px-8 py-3.5 font-semibold rounded-full cursor-pointer"
              >
                Ver Business
              </button>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {Apps.map((app, i) => (
                <a
                  key={i}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 px-7 py-4 bg-white hover:bg-[#F4F7FC] text-[#0B1B3A] rounded-2xl transition-all hover:scale-[1.03] shadow-xl"
                >
                  <img src={app.icon} alt={app.platform} className="w-9 h-9" />
                  <div className="text-left">
                    <p className="text-xs text-[#5B6B86]">{app.description}</p>
                    <p className="text-base font-bold">{app.platform}</p>
                  </div>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section ref={menus[5].ref} className="lo-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold leading-[1.12] tracking-tight text-[#0B1B3A]">
                Preguntas frecuentes
              </h2>
              <p className="mt-5 text-lg lo-muted">
                Todo lo que necesitas saber antes de empezar.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="space-y-0">
              {Faqs.map((faq, i) => (
                <div key={i} className="lo-faq-item">
                  <FaqItem question={faq.question} answer={faq.answer} />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <div className="lo-footer-wrap">
        <Footer />
      </div>
      <WhatsApp />
    </div>
  );
}
